import React from "react";
import { FileText } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { TreatmentPlanStatus } from "@/lib/treatmentPlan/treatmentPlanData";

interface PlanOverviewSectionProps {
  formData: {
    name: string;
    description: string;
    startDate: string;
    endDate: string;
    status: TreatmentPlanStatus;
  };
  onUpdate: (updates: Partial<PlanOverviewSectionProps["formData"]>) => void;
  isDarkMode: boolean;
}

export const PlanOverviewSection: React.FC<PlanOverviewSectionProps> = ({
  formData,
  onUpdate,
  isDarkMode,
}) => {
  return (
    <div className="space-y-4">
      <div>
        <h3
          className={`text-lg font-semibold ${isDarkMode ? "text-gray-200" : "text-gray-900"}`}
        >
          Treatment Plan Overview
        </h3>
        <p
          className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
        >
          Provide basic information about this treatment plan
        </p>
      </div>

      <Card
        className={`${isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"}`}
      >
        <CardHeader>
          <CardTitle
            className={`text-base ${isDarkMode ? "text-gray-200" : "text-gray-900"}`}
          >
            Plan Details
          </CardTitle>
          <CardDescription
            className={isDarkMode ? "text-gray-400" : "text-gray-600"}
          >
            Enter the essential details for this treatment plan
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label
              htmlFor="plan-name"
              className={isDarkMode ? "text-gray-300" : "text-gray-700"}
            >
              Plan Name *
            </Label>
            <Input
              id="plan-name"
              type="text"
              placeholder="e.g., Diabetes Management Plan, Weight Loss Program"
              value={formData.name}
              onChange={(e) => onUpdate({ name: e.target.value })}
              className={`${
                isDarkMode
                  ? "bg-gray-900 border-gray-600 text-gray-200"
                  : "bg-white border-gray-300"
              }`}
            />
            {!formData.name && (
              <p className="text-xs text-red-500">Plan name is required</p>
            )}
          </div>

          <div className="space-y-2">
            <Label
              htmlFor="plan-description"
              className={isDarkMode ? "text-gray-300" : "text-gray-700"}
            >
              Description (optional)
            </Label>
            <Textarea
              id="plan-description"
              placeholder="Provide a detailed description of this treatment plan, including objectives and expected outcomes"
              value={formData.description}
              onChange={(e) => onUpdate({ description: e.target.value })}
              className={`${
                isDarkMode
                  ? "bg-gray-900 border-gray-600 text-gray-200"
                  : "bg-white border-gray-300"
              }`}
              rows={4}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label
                htmlFor="start-date"
                className={isDarkMode ? "text-gray-300" : "text-gray-700"}
              >
                Start Date *
              </Label>
              <Input
                id="start-date"
                type="date"
                value={formData.startDate}
                onChange={(e) => onUpdate({ startDate: e.target.value })}
                className={`${
                  isDarkMode
                    ? "bg-gray-900 border-gray-600 text-gray-200"
                    : "bg-white border-gray-300"
                }`}
              />
              {!formData.startDate && (
                <p className="text-xs text-red-500">Start date is required</p>
              )}
            </div>

            <div className="space-y-2">
              <Label
                htmlFor="end-date"
                className={isDarkMode ? "text-gray-300" : "text-gray-700"}
              >
                End Date (optional)
              </Label>
              <Input
                id="end-date"
                type="date"
                value={formData.endDate}
                onChange={(e) => onUpdate({ endDate: e.target.value })}
                min={formData.startDate}
                className={`${
                  isDarkMode
                    ? "bg-gray-900 border-gray-600 text-gray-200"
                    : "bg-white border-gray-300"
                }`}
              />
              <p
                className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-500"}`}
              >
                Leave blank for ongoing treatment
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <Label
              htmlFor="plan-status"
              className={isDarkMode ? "text-gray-300" : "text-gray-700"}
            >
              Status
            </Label>
            <Select
              value={formData.status}
              onValueChange={(value: TreatmentPlanStatus) =>
                onUpdate({ status: value })
              }
            >
              <SelectTrigger
                className={`${
                  isDarkMode
                    ? "bg-gray-900 border-gray-600 text-gray-200"
                    : "bg-white border-gray-300"
                }`}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="paused">Paused</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <div
        className={`p-4 rounded-lg border ${isDarkMode ? "bg-gray-800 border-gray-700" : "bg-gray-100 border-gray-300"}`}
      >
        <div className="flex items-start gap-3">
          <FileText
            className={`w-5 h-5 mt-0.5 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
          />
          <div>
            <h4
              className={`text-sm font-semibold mb-1 ${isDarkMode ? "text-gray-300" : "text-gray-900"}`}
            >
              Treatment Plan Information
            </h4>
            <p
              className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-700"}`}
            >
              After completing the overview, you'll be able to add goals, schedule
              follow-up appointments, link prescriptions, configure vital tracking,
              and set up subscription billing in the following steps.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
